import { Alert, Box, Button, Link, StatusIndicator, Table } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';

import { CAMPAIGN_STATUS_INDICATOR, Campaign } from '../services/campaigns';

interface CampaignTableProps {
  items: readonly Campaign[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

/**
 * Cloudscape Table listing the available campaigns.
 * Rows navigate to the individual campaign page, where the CSV upload lives.
 * The name cell is a real link so the navigation is keyboard operable and
 * announced by screen readers — onRowClick alone is mouse-only.
 */
export function CampaignTable({ items, isLoading, error, onRetry }: CampaignTableProps) {
  const navigate = useNavigate();

  const openCampaign = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  if (error && !isLoading) {
    return (
      <Alert type="error" action={<Button onClick={onRetry}>Retry</Button>}>
        Unable to load campaigns. Please retry.
      </Alert>
    );
  }

  return (
    <Table
      variant="container"
      items={[...items]}
      trackBy="campaignId"
      loading={isLoading}
      loadingText="Loading campaigns..."
      ariaLabels={{
        tableLabel: 'Campaigns',
      }}
      onRowClick={({ detail }) => openCampaign(detail.item.campaignId)}
      columnDefinitions={[
        {
          id: 'campaignName',
          header: 'Campaign',
          cell: (item) => (
            <Link
              href={`/campaigns/${item.campaignId}`}
              onFollow={(event) => {
                // stopPropagation is forwarded to the underlying DOM event by
                // Cloudscape, so the row's onRowClick does not also fire and
                // push a duplicate history entry.
                event.preventDefault();
                event.stopPropagation();
                openCampaign(item.campaignId);
              }}
            >
              {item.campaignName}
            </Link>
          ),
          width: 220,
        },
        {
          id: 'description',
          header: 'Description',
          cell: (item) => item.description || '—',
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => {
            const indicator = CAMPAIGN_STATUS_INDICATOR[item.status];
            return <StatusIndicator type={indicator.type}>{indicator.label}</StatusIndicator>;
          },
          width: 140,
        },
      ]}
      empty={
        <Box textAlign="center" color="inherit" padding={{ top: 'l', bottom: 'l' }}>
          <b>No campaigns</b>
          <Box variant="p" color="inherit">
            No campaigns have been created yet.
          </Box>
        </Box>
      }
    />
  );
}
